import { createSvgEl } from './svg';
import type { Vec2 } from '@learnforge/demo-math/geometry';
import { sub, normalize, scale, perp, add } from '@learnforge/demo-math/geometry';

export type MarkSpec =
  | {
      kind: 'angle';
      id: string;
      vertex: Vec2;
      from: Vec2;
      to: Vec2;
      strokes?: 1 | 2 | 3;
      label?: string;
      radius?: number;
      /**
       * Draw the REFLEX angle (the major arc, > 180°) instead of the minor arc.
       * Used e.g. for a central angle that subtends the arc opposite the inscribed
       * point (∠AOB = 2·∠APB > 180° when ∠APB > 90°). Default false.
       */
      reflex?: boolean;
      /**
       * Stroke/label colour for this arc (any CSS colour, e.g. 'var(--accent)').
       * Used to disambiguate crowded concentric arcs by highlighting one of them.
       * Defaults to 'currentColor' (the stage --ink).
       */
      color?: string;
    }
  | {
      kind: 'tick';
      id: string;
      a: Vec2;
      b: Vec2;
      count?: 1 | 2 | 3;
    }
  | {
      kind: 'right-angle';
      id: string;
      vertex: Vec2;
      from: Vec2;
      to: Vec2;
      size?: number;
    }
  | {
      kind: 'parallel';
      id: string;
      a: Vec2;
      b: Vec2;
      arrows?: 1 | 2 | 3;
    };

export interface MarksHandle {
  update(marks: readonly MarkSpec[]): void;
  destroy(): void;
}

const DEFAULT_ANGLE_RADIUS = 24;
const DEFAULT_ANGLE_STROKE_GAP = 4;
// How far beyond the arc to push the value label so it sits NEAR the arc
// rather than on top of the vertex/arc line.
const DEFAULT_ANGLE_LABEL_GAP = 13;
const DEFAULT_TICK_SIZE = 6;
const DEFAULT_TICK_GAP = 2;
const DEFAULT_RIGHT_ANGLE_SIZE = 12;
const DEFAULT_ARROW_SIZE = 6;
const DEFAULT_ARROW_GAP = 3;

function updateAngleMark(g: SVGElement, spec: MarkSpec & { kind: 'angle' }): void {
  const radius = spec.radius ?? DEFAULT_ANGLE_RADIUS;
  const strokes = spec.strokes ?? 1;
  const stroke = spec.color ?? 'currentColor';

  // Calculate directions from vertex
  const dirToFrom = normalize(sub(spec.from, spec.vertex));
  const dirToTo = normalize(sub(spec.to, spec.vertex));

  // Calculate the cross product to determine if we go clockwise or counter-clockwise
  const cross = dirToFrom.x * dirToTo.y - dirToFrom.y * dirToTo.x;
  const reflex = spec.reflex ?? false;
  // Minor arc: largeArc=0 and the sweep implied by the cross product. The reflex
  // (major, > 180°) arc is the complement: largeArc=1 and the opposite sweep.
  const minorSweep = cross < 0 ? 0 : 1;
  const largeArcFlag = reflex ? 1 : 0;
  const sweepFlag = reflex ? 1 - minorSweep : minorSweep;

  // Clear existing children
  while (g.firstChild) {
    g.removeChild(g.firstChild);
  }

  // Render concentric arcs
  for (let i = 0; i < strokes; i++) {
    const r = radius + i * DEFAULT_ANGLE_STROKE_GAP;
    const start = add(spec.vertex, scale(dirToFrom, r));
    const end = add(spec.vertex, scale(dirToTo, r));

    const arcPath = createSvgEl('path', {
      d: `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} ${sweepFlag} ${end.x} ${end.y}`,
      'data-mark-id': spec.id,
      class: 'geom-mark-angle',
      fill: 'none',
      stroke,
      'stroke-width': '1.5',
    });
    g.appendChild(arcPath);
  }

  // Add optional label
  if (spec.label) {
    // Place the label just OUTSIDE the (outermost) arc along the bisector, so
    // the value reads near the arc instead of sitting on the vertex.
    const outerRadius = radius + (strokes - 1) * DEFAULT_ANGLE_STROKE_GAP;
    const midRadius = outerRadius + DEFAULT_ANGLE_LABEL_GAP;

    // Calculate the direction of the summed unit vectors for robust bisector
    let bisectorDir = normalize(add(dirToFrom, dirToTo));
    // Handle degenerate opposite-rays case where the sum is ~0
    if (bisectorDir.x === 0 && bisectorDir.y === 0) {
      bisectorDir = perp(dirToFrom);
    }
    // The reflex arc opens on the opposite side, so place its label there.
    if (reflex) {
      bisectorDir = scale(bisectorDir, -1);
    }

    const labelPos = add(spec.vertex, scale(bisectorDir, midRadius));

    const text = createSvgEl('text', {
      x: labelPos.x,
      y: labelPos.y,
      'text-anchor': 'middle',
      'dominant-baseline': 'middle',
      'data-mark-id': spec.id,
      class: 'geom-mark-label',
      'font-size': '11',
      fill: stroke,
    });
    text.textContent = spec.label;
    g.appendChild(text);
  }
}

function createAngleMark(spec: MarkSpec & { kind: 'angle' }): SVGElement {
  const g = createSvgEl('g', { 'data-mark-id': spec.id });
  updateAngleMark(g, spec);
  return g;
}

function updateTickMark(g: SVGElement, spec: MarkSpec & { kind: 'tick' }): void {
  const count = spec.count ?? 1;
  const tickLength = DEFAULT_TICK_SIZE;

  // Calculate segment direction and perpendicular
  const segmentDir = normalize(sub(spec.b, spec.a));
  const perpDir = perp(segmentDir);

  // Midpoint of the segment
  const midpoint = {
    x: (spec.a.x + spec.b.x) / 2,
    y: (spec.a.y + spec.b.y) / 2,
  };

  // Clear existing children
  while (g.firstChild) {
    g.removeChild(g.firstChild);
  }

  // Render concentric ticks
  for (let i = 0; i < count; i++) {
    const offset = (i - (count - 1) / 2) * DEFAULT_TICK_GAP;
    const tickCenter = add(midpoint, scale(segmentDir, offset));

    const halfLen = tickLength / 2;
    const p1 = add(tickCenter, scale(perpDir, -halfLen));
    const p2 = add(tickCenter, scale(perpDir, halfLen));

    const line = createSvgEl('line', {
      x1: p1.x,
      y1: p1.y,
      x2: p2.x,
      y2: p2.y,
      'data-mark-id': spec.id,
      class: 'geom-mark-tick',
      stroke: 'currentColor',
      'stroke-width': '1.5',
    });
    g.appendChild(line);
  }
}

function createTickMark(spec: MarkSpec & { kind: 'tick' }): SVGElement {
  const g = createSvgEl('g', { 'data-mark-id': spec.id });
  updateTickMark(g, spec);
  return g;
}

function updateRightAngleMark(g: SVGElement, spec: MarkSpec & { kind: 'right-angle' }): void {
  const size = spec.size ?? DEFAULT_RIGHT_ANGLE_SIZE;

  // Calculate unit directions along the two rays
  const dirToFrom = normalize(sub(spec.from, spec.vertex));
  const dirToTo = normalize(sub(spec.to, spec.vertex));

  // Build the four corners of the square
  const p1 = spec.vertex;
  const p2 = add(spec.vertex, scale(dirToFrom, size));
  const p3 = add(p2, scale(dirToTo, size));
  const p4 = add(spec.vertex, scale(dirToTo, size));

  const pathData = `M ${p1.x} ${p1.y} L ${p2.x} ${p2.y} L ${p3.x} ${p3.y} L ${p4.x} ${p4.y} Z`;

  // Clear existing children
  while (g.firstChild) {
    g.removeChild(g.firstChild);
  }

  const path = createSvgEl('path', {
    d: pathData,
    'data-mark-id': spec.id,
    class: 'geom-mark-right-angle',
    fill: 'none',
    stroke: 'currentColor',
    'stroke-width': '1.5',
  });
  g.appendChild(path);
}

function createRightAngleMark(spec: MarkSpec & { kind: 'right-angle' }): SVGElement {
  const g = createSvgEl('g', { 'data-mark-id': spec.id });
  updateRightAngleMark(g, spec);
  return g;
}

function updateParallelMark(g: SVGElement, spec: MarkSpec & { kind: 'parallel' }): void {
  const arrows = spec.arrows ?? 1;
  const arrowSize = DEFAULT_ARROW_SIZE;

  // Calculate segment direction
  const segmentDir = normalize(sub(spec.b, spec.a));
  const perpDir = perp(segmentDir);

  // Midpoint of the segment
  const midpoint = {
    x: (spec.a.x + spec.b.x) / 2,
    y: (spec.a.y + spec.b.y) / 2,
  };

  // Clear existing children
  while (g.firstChild) {
    g.removeChild(g.firstChild);
  }

  // Render concentric chevrons
  for (let i = 0; i < arrows; i++) {
    const offset = (i - (arrows - 1) / 2) * DEFAULT_ARROW_GAP;
    const tip = add(midpoint, scale(perpDir, offset));

    // Symmetric V-shape: tip at chevron centre, barbs pointing backward from segment direction
    const h = arrowSize / 2;
    const p1 = add(tip, sub(scale(segmentDir, -h), scale(perpDir, h)));
    const p2 = tip;
    const p3 = add(tip, add(scale(segmentDir, -h), scale(perpDir, h)));

    const polyline = createSvgEl('polyline', {
      points: `${p1.x},${p1.y} ${p2.x},${p2.y} ${p3.x},${p3.y}`,
      'data-mark-id': spec.id,
      class: 'geom-mark-parallel',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.5',
    });
    g.appendChild(polyline);
  }
}

function createParallelMark(spec: MarkSpec & { kind: 'parallel' }): SVGElement {
  const g = createSvgEl('g', { 'data-mark-id': spec.id });
  updateParallelMark(g, spec);
  return g;
}

export function createMarks(parent: SVGElement): MarksHandle {
  const nodeMap = new Map<string, SVGElement>();

  function createMark(spec: MarkSpec): SVGElement {
    switch (spec.kind) {
      case 'angle':
        return createAngleMark(spec);
      case 'tick':
        return createTickMark(spec);
      case 'right-angle':
        return createRightAngleMark(spec);
      case 'parallel':
        return createParallelMark(spec);
    }
  }

  function updateMark(node: SVGElement, spec: MarkSpec): void {
    switch (spec.kind) {
      case 'angle':
        updateAngleMark(node, spec);
        break;
      case 'tick':
        updateTickMark(node, spec);
        break;
      case 'right-angle':
        updateRightAngleMark(node, spec);
        break;
      case 'parallel':
        updateParallelMark(node, spec);
        break;
    }
  }

  return {
    update(marks: readonly MarkSpec[]): void {
      const newIds = new Set(marks.map((m) => m.id));

      // Remove marks not in the new set
      for (const [id, node] of nodeMap.entries()) {
        if (!newIds.has(id)) {
          if (node.parentNode) {
            node.parentNode.removeChild(node);
          }
          nodeMap.delete(id);
        }
      }

      // Update or add marks
      for (const spec of marks) {
        if (nodeMap.has(spec.id)) {
          // Mark exists, update its geometry in place
          const node = nodeMap.get(spec.id)!;
          updateMark(node, spec);
        } else {
          // New mark
          const node = createMark(spec);
          parent.appendChild(node);
          nodeMap.set(spec.id, node);
        }
      }
    },

    destroy(): void {
      for (const node of nodeMap.values()) {
        if (node.parentNode) {
          node.parentNode.removeChild(node);
        }
      }
      nodeMap.clear();
    },
  } satisfies MarksHandle;
}
